import React, { Fragment } from 'react';
import { graphql, Link } from 'gatsby';
import PropTypes from 'prop-types';
import kebabCase from 'lodash.kebabcase';

import {
  PageHeader,
  TaxFilter,
  SimpleDataTable,
  Section,
  TaxAppendixTOC,
  IntroSectionSummary,
  clip
} from '@budget-monorepo/shared-ui';
import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';

const TaxType = (props) => {
  const { location, data } = props;
  const {
    introduction,
    content,
    site,
    filter,
    toc
  } = data;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent;
  const layoutProps = {
    activeIndex: 0,
    pre: () => <TaxFilter {...{ location, filter }} />,
    above: () => <PageHeader title={content.title} category={content.budgetSection} />,
    pageTitle: content.title,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime
  };
  const allData = content.summaryTable.data.map((row) => {
    const innerData = {
      /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
      taxType: (<Link to={`${content.fields.baseSectionURL}${kebabCase(row.taxType)}`}>{row.taxType}</Link>),
      ...row.total
    };
    return({
      ...innerData
    });
  });
  const summaryTable = {
    headers: content.totalTable.headers,
    total: content.totalTable.total,
    data: allData
  };

  // Get the content of the first p tag to use as tax intro summary
  const introSummary = introduction.data.find((item) => item.name === 'p').content;
  // metadata for the page
  const metaTitle = content.title;
  const metaDescription = clip(introSummary, 160);
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        metaType: 'dataset',
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        <IntroSectionSummary introText={introSummary} link={introduction.fields.slug} charLimit={500} />
        <Section>
          <h3>{content.totalTable.title}</h3>
          <SimpleDataTable title={`${content.title} ${content.totalTable.title}`} table={summaryTable} maxColWidth={100} mantissa={1} download={location.pathname} />
        </Section>
        <TaxAppendixTOC data={toc} />
      </Layout>
    </Fragment>
  );
};

TaxType.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object
};

export default TaxType;

export const query = graphql`
  query taxExpenditureQuery {
    site {
      ...SiteMetadata
    }
    # Tax introduction data are generated as GenericJson by gatsby-plugin-create-generic-pages
    # To query the introduction page for the tax expenditure landing page, filter generic by the slug to match its introduction GenericJson node
    introduction: genericJson(fields: {slug: {eq: "/tax-expenditure-budget/introduction"}}) {
      title
      fields {
        slug
      }
      data {
        content
        name
      }
    }
    content: pagesJson(fields: {slug: {eq: "/tax-expenditure-budget/"}}) {
      title
      budgetSection
      ...basePagesFields
      totalTable {
        title
        headers {
          name
          type
          align
          value
        }
        total {
          taxType
          fyFourYearsAgo
          fyThreeYearsAgo
          fyTwoYearsAgo
          fyOneYearsAgo
          fyThisYear
        }
      }
      summaryTable {
        data {
          taxType
          total {
            taxExpenditure
            fyFourYearsAgo
            fyThreeYearsAgo
            fyTwoYearsAgo
            fyOneYearsAgo
            fyThisYear
          }
        }
      }
      # Get the last modified timestamp for the file powering the page
      ...PagesJsonDataTimeStamp
    }
    filter: taxItemJson {
      data {
        taxType
        taxExpenditure
        taxItemNumber
        taxItemName
      }
    }
    # Tax appenices data are generated as GenericJson by gatsby-plugin-create-generic-pages
    # For the TOC, get all generic data that has category field start with Appendix, e.g. Appendix A, Appendix B and Appendix D and sort them ascendantly
    # Look in src/data/docs/generics/meta.json for the category fields
    toc: allGenericJson(filter: {fields: {category: {regex: "/Appendix.*/"}}}, sort: {fields: fields___category, order: ASC}) {
      edges {
        node {
          fields {
            slug
            category
            title
          }
        }
      }
    }
  }
`;
